import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import inventoryService from '../services/inventoryService';
import {
  X, Camera, Upload, Scissors, Box, Layers, CheckCircle2,
  AlertCircle, Printer, QrCode, MapPin, Tag, RefreshCw,
  FileText, Sparkles, Loader2, Image as ImageIcon, Eye
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  DISPONIVEL: { label: 'Disponível', bg: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  EM_USO: { label: 'Em Uso', bg: 'bg-blue-100 text-blue-800 border-blue-300' },
  RESERVADO: { label: 'Reservado', bg: 'bg-amber-100 text-amber-800 border-amber-300' },
  DESCARTADO: { label: 'Descartado / Perda', bg: 'bg-red-100 text-red-800 border-red-300' }
};

const CONDICAO_OPTIONS = [
  { value: 'INTEIRA', label: 'Peça Inteira Original' },
  { value: 'PEDACO_CORTE', label: 'Pedaço de Corte / Sobra Aproveitável' },
  { value: 'AVARIA_CANTO', label: 'Canto Avariado / Lascado' },
  { value: 'ARRANHOES', label: 'Superfície com Arranhões Leves' },
  { value: 'OUTRO', label: 'Outro Detalhe' }
];

const IndividualItemManagerModal = ({ variant, product, isOpen, onClose, onStockUpdated }) => {
  const queryClient = useQueryClient();
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [generatingMissing, setGeneratingMissing] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Form State do item selecionado
  const [itemForm, setItemForm] = useState({
    is_pedaco: false,
    comprimento_cm: '',
    largura_cm: '',
    espessura_mm: '',
    condicao: 'INTEIRA',
    status_item: 'DISPONIVEL',
    localizacao: '',
    observacoes: ''
  });

  const variantId = variant?.id;

  // Buscar itens individuais da variante
  const { data: itemsResponse, isLoading, refetch } = useQuery({
    queryKey: ['individual-items', variantId],
    queryFn: () => inventoryService.getIndividualItemsByVariant(variantId),
    enabled: isOpen && !!variantId
  });

  const items = itemsResponse?.data || [];

  // Sincronizar item selecionado inicial
  useEffect(() => {
    if (items.length > 0) {
      if (!selectedItem || !items.some(i => i.id === selectedItem.id)) {
        loadItemIntoForm(items[0]);
      } else {
        const fresh = items.find(i => i.id === selectedItem.id);
        if (fresh) loadItemIntoForm(fresh);
      }
    } else {
      setSelectedItem(null);
    }
  }, [items]);

  const loadItemIntoForm = (item) => {
    setSelectedItem(item);
    setItemForm({
      is_pedaco: item.is_pedaco || false,
      comprimento_cm: item.comprimento_cm !== null && item.comprimento_cm !== undefined ? item.comprimento_cm : (variant?.comprimento_cm || ''),
      largura_cm: item.largura_cm !== null && item.largura_cm !== undefined ? item.largura_cm : (variant?.largura_cm || ''),
      espessura_mm: item.espessura_mm !== null && item.espessura_mm !== undefined ? item.espessura_mm : (variant?.espessura_mm || ''),
      condicao: item.condicao || 'INTEIRA',
      status_item: item.status_item || 'DISPONIVEL',
      localizacao: item.localizacao || '',
      observacoes: item.observacoes || ''
    });
  };

  // Mutation para atualizar dados do item
  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data }) => inventoryService.updateIndividualItem(itemId, data),
    onSuccess: (data) => {
      toast.success('Peça atualizada com sucesso!');
      queryClient.invalidateQueries(['individual-items', variantId]);
      refetch();
    },
    onError: (err) => {
      toast.error('Erro ao salvar item: ' + (err.response?.data?.message || err.message));
    }
  });

  // Salvar formulário do item
  const handleSaveItem = (e) => {
    e.preventDefault();
    if (!selectedItem) return;

    updateItemMutation.mutate({
      itemId: selectedItem.id,
      data: itemForm
    });
  };

  // Upload de foto do item específico
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedItem) return;

    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        await inventoryService.uploadIndividualItemPhoto(selectedItem.id, base64);
        toast.success('📷 Foto do item anexada com sucesso!');
        queryClient.invalidateQueries(['individual-items', variantId]);
        refetch();
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Erro ao enviar foto: ' + (err.response?.data?.message || err.message));
      setUploadingPhoto(false);
    }
  };

  // Gerar itens individuais faltantes
  const handleGenerateMissing = async () => {
    const stockQty = parseInt(variant?.estoque_disponivel || 0, 10);
    const missing = Math.max(1, stockQty - items.length);

    setGeneratingMissing(true);
    try {
      await inventoryService.generateMissingVariantItems(variantId, missing);
      toast.success(`${missing} nova(s) peça(s) gerada(s) com códigos individuais!`);
      queryClient.invalidateQueries(['individual-items', variantId]);
      refetch();
    } catch (err) {
      toast.error('Erro ao gerar peças: ' + (err.response?.data?.message || err.message));
    } finally {
      setGeneratingMissing(false);
    }
  };

  // Imprimir etiqueta da peça individual
  const handlePrintLabel = async (qrCode) => {
    try {
      const res = await inventoryService.printSmallLabel(qrCode);
      if (res.success && res.data?.html) {
        const printWindow = window.open('', '_blank', 'width=350,height=400');
        printWindow.document.write(res.data.html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    } catch (err) {
      toast.error('Erro ao abrir impressão da etiqueta');
    }
  };

  if (!isOpen || !variant) return null;

  const filteredItems = items.filter(i => {
    if (filterStatus === 'ALL') return true;
    if (filterStatus === 'PEDACO') return i.is_pedaco === true;
    return i.status_item === filterStatus;
  });

  // Área calculada se houver comprimento e largura
  const comp = parseFloat(itemForm.comprimento_cm) || 0;
  const larg = parseFloat(itemForm.largura_cm) || 0;
  const areaM2 = comp > 0 && larg > 0 ? ((comp * larg) / 10000).toFixed(4) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-auto max-h-[92vh] overflow-hidden flex flex-col border border-gray-200">
        
        {/* Top Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white px-5 sm:px-8 py-4 flex items-center justify-between shrink-0 shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-amber-500/80 border border-amber-300/40 flex items-center justify-center shadow-inner text-white font-bold">
              <Box className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/30 text-amber-200 border border-amber-300/30">
                  Rastreamento Unitário
                </span>
                <span className="text-xs text-slate-300">Variante ID #{variant.id}</span>
              </div>
              <h2 className="text-lg sm:text-xl font-black text-white mt-0.5">
                {product?.nome || variant.produto_nome || 'Produto'} • {variant.cor || variant.tamanho_label || variant.sku_variacao}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
              title="Atualizar lista de peças"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Barra de Sub-Header com Estatísticas e Ações Rápidas */}
        <div className="bg-slate-50 px-5 sm:px-8 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-gray-600">
              Estoque Físico Registrado: <strong className="text-gray-900 font-mono text-sm">{items.length} peças</strong>
            </span>
            <span className="text-gray-600">
              Saldo no Sistema: <strong className="text-blue-700 font-mono text-sm">{variant.estoque_disponivel || 0} un</strong>
            </span>
            <span className="text-gray-600">
              Pedaços / Sobras: <strong className="text-amber-800 font-mono text-sm">{items.filter(i => i.is_pedaco).length} un</strong>
            </span>
          </div>

          {items.length < parseInt(variant.estoque_disponivel || 0, 10) && (
            <button
              type="button"
              onClick={handleGenerateMissing}
              disabled={generatingMissing}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center gap-1.5 shadow-2xs transition-colors"
            >
              {generatingMissing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              <span>Gerar {parseInt(variant.estoque_disponivel || 0, 10) - items.length} Peça(s) Faltante(s)</span>
            </button>
          )}
        </div>

        {/* Conteúdo Principal: Split View (Master / Detail) */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-12">
          
          {/* Coluna Esquerda (Lista de Peças Físicas) */}
          <div className="md:col-span-5 border-r border-gray-200 flex flex-col h-full bg-gray-50/60 overflow-hidden">
            {/* Filtros da Lista */}
            <div className="p-3 border-b border-gray-200 flex items-center justify-between gap-2 bg-white">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Peças ({filteredItems.length})</span>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-300 rounded-lg bg-white"
              >
                <option value="ALL">Todos os status</option>
                <option value="DISPONIVEL">Apenas Disponíveis</option>
                <option value="PEDACO">Apenas Pedaços / Cortes</option>
                <option value="EM_USO">Em Uso</option>
                <option value="RESERVADO">Reservados</option>
              </select>
            </div>

            {/* Lista com Scroll */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
              {isLoading ? (
                <div className="p-8 text-center text-gray-500 flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <p className="text-xs">Carregando peças físicas...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="p-8 text-center text-gray-500 space-y-2">
                  <Box className="w-10 h-10 text-gray-300 mx-auto" />
                  <p className="text-xs font-semibold text-gray-700">Nenhuma peça encontrada</p>
                  <p className="text-[11px] text-gray-400">Clique em "Gerar Peças Faltantes" para criar os identificadores desta variante.</p>
                </div>
              ) : (
                filteredItems.map((item) => {
                  const isSelected = selectedItem?.id === item.id;
                  const statusInfo = STATUS_CONFIG[item.status_item] || STATUS_CONFIG.DISPONIVEL;

                  return (
                    <div
                      key={item.id}
                      onClick={() => loadItemIntoForm(item)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center gap-3 ${
                        isSelected
                          ? 'bg-white border-blue-500 shadow-md ring-2 ring-blue-500/20'
                          : 'bg-white hover:bg-gray-50/80 border-gray-200 shadow-2xs'
                      }`}
                    >
                      {/* Miniatura ou Ícone */}
                      <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                        {item.photo_url ? (
                          <img src={item.photo_url} alt="Foto da peça" className="w-full h-full object-cover" />
                        ) : item.is_pedaco ? (
                          <Scissors className="w-5 h-5 text-amber-600" />
                        ) : (
                          <Box className="w-5 h-5 text-blue-600" />
                        )}
                      </div>

                      {/* Informações Resumidas */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-mono text-xs font-bold text-gray-900 truncate">
                            {item.numero_serie || `ID #${item.id}`}
                          </span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${statusInfo.bg}`}>
                            {statusInfo.label}
                          </span>
                        </div>

                        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                          {item.is_pedaco ? (
                            <span className="text-amber-800 font-bold bg-amber-50 px-1 rounded border border-amber-200">
                              ✂️ Pedaço ({item.comprimento_cm || '?'} × {item.largura_cm || '?'}cm)
                            </span>
                          ) : (
                            <span>Peça Inteira</span>
                          )}
                        </div>

                        {item.localizacao_exibicao && (
                          <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{item.localizacao_exibicao}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Coluna Direita (Detalhes & Edição da Peça Selecionada) */}
          <div className="md:col-span-7 flex flex-col h-full bg-white overflow-hidden">
            {selectedItem ? (
              <form onSubmit={handleSaveItem} className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
                  
                  {/* Header do Item Selecionado */}
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded border border-indigo-200">
                          {selectedItem.numero_serie || `Item #${selectedItem.id}`}
                        </span>
                        <span className="text-[11px] font-mono text-gray-500">QR: {selectedItem.qr_code}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Armazém: {selectedItem.armazem_nome || 'Armazém Padrão'}</p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handlePrintLabel(selectedItem.qr_code)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-bold flex items-center gap-1.5 border border-gray-300 shadow-2xs transition-colors"
                      title="Imprimir etiqueta adesiva com QR code"
                    >
                      <Printer className="w-3.5 h-3.5 text-gray-600" />
                      <span>Imprimir Etiqueta</span>
                    </button>
                  </div>

                  {/* Foto do Item Específico */}
                  <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2.5">
                    <label className="block text-xs font-bold text-gray-700 uppercase">
                      Foto Real / Inspeção da Peça
                    </label>

                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-inner">
                        {uploadingPhoto ? (
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        ) : selectedItem.photo_url ? (
                          <img src={selectedItem.photo_url} alt="Foto real" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-gray-300" />
                        )}
                      </div>

                      <div className="space-y-1.5 text-xs">
                        <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-gray-50 text-gray-800 rounded-lg border border-gray-300 font-bold cursor-pointer shadow-2xs transition-colors">
                          <Camera className="w-4 h-4 text-purple-600" />
                          <span>{selectedItem.photo_url ? 'Alterar Foto' : 'Tirar ou Enviar Foto'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                        <p className="text-[11px] text-gray-500">
                          Fotografe o pedaço, o corte, o veio da chapa ou eventuais marcas para identificação visual rápida na fábrica.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Toggle: Peça Inteira vs Pedaço / Corte */}
                  <div className="p-3.5 bg-amber-50/70 rounded-xl border border-amber-200 space-y-3">
                    <label className="flex items-center gap-2.5 cursor-pointer font-bold text-xs text-amber-950">
                      <input
                        type="checkbox"
                        checked={itemForm.is_pedaco}
                        onChange={(e) => setItemForm({ ...itemForm, is_pedaco: e.target.checked })}
                        className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500"
                      />
                      <span>Esta peça é um pedaço / corte de uma chapa original?</span>
                    </label>

                    {itemForm.is_pedaco && (
                      <div className="space-y-2 pt-2 border-t border-amber-200/60">
                        <p className="text-[11px] text-amber-900">
                          Informe as medidas exatas deste pedaço restante para que possa ser aproveitado em novos cortes no plano de corte:
                        </p>

                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Comprimento (cm) *</label>
                            <input
                              type="number"
                              step="0.1"
                              value={itemForm.comprimento_cm}
                              onChange={(e) => setItemForm({ ...itemForm, comprimento_cm: e.target.value })}
                              placeholder="Ex: 120.0"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg font-mono font-bold bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Largura (cm) *</label>
                            <input
                              type="number"
                              step="0.1"
                              value={itemForm.largura_cm}
                              onChange={(e) => setItemForm({ ...itemForm, largura_cm: e.target.value })}
                              placeholder="Ex: 80.0"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg font-mono font-bold bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-700 uppercase mb-0.5">Espessura (mm)</label>
                            <input
                              type="number"
                              step="0.1"
                              value={itemForm.espessura_mm}
                              onChange={(e) => setItemForm({ ...itemForm, espessura_mm: e.target.value })}
                              placeholder="Ex: 15.0"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg font-mono font-bold bg-white"
                            />
                          </div>
                        </div>

                        {areaM2 && (
                          <div className="text-[11px] font-bold text-blue-900 bg-blue-50 px-2.5 py-1 rounded border border-blue-200 flex justify-between">
                            <span>Área Útil do Pedaço:</span>
                            <span className="font-mono">{areaM2} m²</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Condição & Status da Peça */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <label className="block font-bold text-gray-700 uppercase mb-1">Condição Física da Peça</label>
                      <select
                        value={itemForm.condicao}
                        onChange={(e) => setItemForm({ ...itemForm, condicao: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      >
                        {CONDICAO_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-bold text-gray-700 uppercase mb-1">Status no Estoque</label>
                      <select
                        value={itemForm.status_item}
                        onChange={(e) => setItemForm({ ...itemForm, status_item: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white font-semibold"
                      >
                        <option value="DISPONIVEL">Disponível no Armazém</option>
                        <option value="EM_USO">Em Uso / Na Bancada</option>
                        <option value="RESERVADO">Reservado para Projeto</option>
                        <option value="DESCARTADO">Descartado / Perda</option>
                      </select>
                    </div>
                  </div>

                  {/* Localização Física Exata */}
                  <div className="text-xs">
                    <label className="block font-bold text-gray-700 uppercase mb-1">
                      Localização Física Exata (Prateleira / Cavalete)
                    </label>
                    <input
                      type="text"
                      value={itemForm.localizacao}
                      onChange={(e) => setItemForm({ ...itemForm, localizacao: e.target.value })}
                      placeholder="Ex: Cavalete de Sobras 2, Prateleira MDF 15mm..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    />
                  </div>

                  {/* Observações da Peça */}
                  <div className="text-xs">
                    <label className="block font-bold text-gray-700 uppercase mb-1">
                      Observações & Detalhes da Peça
                    </label>
                    <textarea
                      rows="2"
                      value={itemForm.observacoes}
                      onChange={(e) => setItemForm({ ...itemForm, observacoes: e.target.value })}
                      placeholder="Ex: Sobra do projeto do closet, fita de borda colada em um dos lados..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white"
                    />
                  </div>
                </div>

                {/* Footer com Salvar */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3 shrink-0">
                  <button
                    type="submit"
                    disabled={updateItemMutation.isPending}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 shadow-md transition-all"
                  >
                    {updateItemMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    <span>Salvar Dados da Peça</span>
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-400 space-y-2">
                <Box className="w-12 h-12 text-gray-300" />
                <p className="text-sm font-semibold text-gray-600">Selecione uma peça na lista ao lado</p>
                <p className="text-xs text-gray-400">Você poderá editar dimensões de pedaços, tirar foto e adicionar detalhes específicos.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualItemManagerModal;
